/**
 * Tenant Config Service
 * Admin API calls for tenant feature flags, limits, and access controls
 */

import api from './api';

const tenantConfigService = {
  /**
   * Get full tenant configuration
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} Full config
   */
  getConfig: async (tenantId) => {
    const response = await api.get(`/v1/admin/tenants/${tenantId}/config`);
    return response.data.config;
  },

  /**
   * Update tenant configuration (partial merge)
   * @param {string} tenantId - Tenant ID
   * @param {Object} updates - Partial updates
   * @returns {Promise<Object>} Updated config
   */
  updateConfig: async (tenantId, updates) => {
    const response = await api.put(`/v1/admin/tenants/${tenantId}/config`, updates);
    return response.data.config;
  },

  /**
   * Get feature flags only
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} Feature flags
   */
  getFeatureFlags: async (tenantId) => {
    const response = await api.get(`/v1/admin/tenants/${tenantId}/config/feature-flags`);
    return response.data.feature_flags;
  },

  /**
   * Update feature flags
   * @param {string} tenantId - Tenant ID
   * @param {Object} flags - Feature flags
   * @returns {Promise<Object>} Updated flags
   */
  updateFeatureFlags: async (tenantId, flags) => {
    const response = await api.put(`/v1/admin/tenants/${tenantId}/config/feature-flags`, flags);
    return response.data.feature_flags;
  },

  /**
   * Get limits only
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} Limits
   */
  getLimits: async (tenantId) => {
    const response = await api.get(`/v1/admin/tenants/${tenantId}/config/limits`);
    return response.data.limits;
  },

  /**
   * Update limits
   * @param {string} tenantId - Tenant ID
   * @param {Object} limits - Limits
   * @returns {Promise<Object>} Updated limits
   */
  updateLimits: async (tenantId, limits) => {
    const response = await api.put(`/v1/admin/tenants/${tenantId}/config/limits`, limits);
    return response.data.limits;
  },

  /**
   * Get access controls only
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} Access controls
   */
  getAccess: async (tenantId) => {
    const response = await api.get(`/v1/admin/tenants/${tenantId}/config/access`);
    return response.data.access;
  },

  /**
   * Update access controls
   * @param {string} tenantId - Tenant ID
   * @param {Object} access - Access controls
   * @returns {Promise<Object>} Updated access
   */
  updateAccess: async (tenantId, access) => {
    const response = await api.put(`/v1/admin/tenants/${tenantId}/config/access`, access);
    return response.data.access;
  },
};

export default tenantConfigService;
